import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { validatePurchaseFields } from '../../../utils/constants/purchase-validation-fields';
import {
    tryToCreatePurchase,
    tryToEditPurchase,
    tryToFetchSinglePurchase
} from '../../../store/brokers/partner/purchases/PurchasesSlice';
import { tryToFetchAssetCurrencies } from '../../../store/brokers/partner/assets/assetsSlice';
import { tryToFetchAllSuppliers } from '../../../store/brokers/admin/suppliers/SuppliersSlice';
import ConfirmPurchaseCreate from './modals/ConfirmPurchaseCreate';
import FieldsStep from './form-steps/FieldsStep';
import ItemsStep from './form-steps/ItemsStep';
import Button from '../../../shared/button';
import Error from '../../../shared/error';


const PurchaseForm = () => {
    const { id } = useParams();
    const navigator = useNavigate();
    const dispatch = useAppDispatch();
    const [allRowsItems, setAllRowsItems] = useState<any>([{}]);
    const [allRowsData, setAllRowsData] = useState<any>([{}]);
    const [itemsErrors, setItemsErrors] = useState<any>();
    const [errorStep, setErrorStep] = useState<string>();
    const [suppliers, setSuppliers] = useState<any>();
    const [itemRowChanged, setItemRowChanged] = useState<number>();
    const [itemsLoading, setItemsLoading] = useState<{ loading: boolean; row: number; }>();
    const [formFieldsData, setFormFieldsData] = useState<any>();
    const [formFieldsErrors, setFormFieldsErrors] = useState<any>();
    const [itemsGeneralError, setItemsGeneralError] = useState<string>();
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);
    const [currencies, setCurrencies] = useState<any>()
    const state = useAppSelector((state) => state.purchases);
    const stateCurrencies: any = useAppSelector((state) => state.assets.currencies)

    const getPurchaseDetailsById = async () => {
        await dispatch(tryToFetchSinglePurchase(id || ''));
    };

    const onGettingAllSuppliers = async () => {
        try {
            const response: any = await dispatch(tryToFetchAllSuppliers()).unwrap()
            if (response) {
                const formatSuppliers = (response && response?.length > 0) && response?.map((item: any) => ({ ...item || {}, value: item?._id, label: item?.name || item?.username }))
                setSuppliers(formatSuppliers || [])
            }
        } catch (err) {
            // error
        }
    }

    const onGettingAllCurrencies = async () => {
        try {
            const response: any = await dispatch(tryToFetchAssetCurrencies()).unwrap()
            if (response) {
                const formatCurrencies = (response && response?.data?.length > 0) && response?.data?.map((item: any) => ({ ...item || {}, value: item?._id, label: item?.descriptor?.name }))
                setCurrencies(formatCurrencies || [])
            }
        } catch (err) {
            //
        }
    }

    useEffect(() => {
        onGettingAllSuppliers();
        onGettingAllCurrencies()
    }, [])

    useEffect(() => {
        if (state.purchaseDetails && id) {
            setFormFieldsData({
                // supplier: state?.purchaseDetails?.supplierInfo,
                shippingCosts: state?.purchaseDetails?.purchaseInfo?.shippingCosts,
                expectedDeliveryDate: `${moment(state?.purchaseDetails?.expectedDeliveryDate).format('YYYY-MM-DD')}`
            });
            setAllRowsData(state?.purchaseDetails?.items || undefined);
            setAllRowsItems(state?.purchaseDetails?.items || undefined);
        } else {
            setFormFieldsData(undefined);
            setAllRowsData(undefined);
            setAllRowsItems(undefined);
        }
    }, [state.purchaseDetails]);

    useEffect(() => {
        if (id && suppliers && state?.purchaseDetails) {
            const findSupplier = suppliers && suppliers?.length > 0 && suppliers?.find((item: any) => item?._id === state?.purchaseDetails?.supplierInfo?.id)
            const findCurrency = currencies && currencies?.length > 0 && currencies?.find((item: any) => item?.descriptor?.name === state?.purchaseDetails?.currency)

            setFormFieldsData((prevData: any) => ({
                ...prevData || {},
                supplier: findSupplier,
                currency: findCurrency,
            }));
        }
    }, [suppliers, state?.purchaseDetails, id])

    useEffect(() => {
        if (id) {
            getPurchaseDetailsById();
        } else {
            setAllRowsData(undefined);
            setAllRowsItems(undefined);
            setFormFieldsData(undefined);
        }
    }, [id]);

    const onClickAddItems = () => {
        setAllRowsItems([...(allRowsItems || []), {}]);
        setAllRowsData([
            ...(allRowsData || []),
            {
                // index: allRowsItems?.length || 0
            },
        ]);
    };

    const areObjectsEqual = (obj1: any, obj2: any) => {
        const stringifiedObj1 = obj1 ? JSON.stringify(obj1, Object.keys(obj1).sort()) : null
        const stringifiedObj2 = obj2 ? JSON.stringify(obj2, Object.keys(obj2).sort()) : null
        return stringifiedObj1 === stringifiedObj2;
    }

    const onDeleteItemRow = (id: number) => {
        if (id === 0 && allRowsItems?.length === 1) {
            setAllRowsData(undefined);
            setItemsErrors(undefined);
            setItemRowChanged(0);
            return;
        }
        const findItemPerCategory = (allRowsItems && allRowsItems?.length > 0) ? allRowsItems?.find((item: any, idx: number) => idx === id) : [];
        const allRowItemsData = (allRowsItems && allRowsItems?.length > 0) ? allRowsItems?.filter((item: any, idx: number) => !((areObjectsEqual(item, findItemPerCategory)))) : [];
        const findDataPerCategory = (allRowsData && allRowsData?.length > 0) ? allRowsData?.find((item: any, idx: number) => idx === id) : [];
        const allRowsDataFilter = (allRowsData && allRowsData?.length > 0) ? allRowsData?.filter((item: any, idx: number) => !areObjectsEqual(item, findDataPerCategory)) : [];
        const filterItemErrors = itemsErrors && itemsErrors?.length > 0 && itemsErrors?.filter((item: any) => (item?.index !== id));
        setAllRowsData(allRowsDataFilter || []);
        setItemsErrors(filterItemErrors || undefined);
        setAllRowsItems(allRowItemsData || []);
    };

    const onCloneRow = (id: number) => {
        const cloneRow = allRowsItems?.[id];
        const cloneData = (allRowsData && allRowsData?.length > 0) && allRowsData?.find((row: any, idx: number) => idx === id);
        const formatAllItems = allRowsItems ? [...(allRowsData || []), cloneRow || {}] : [{}, cloneData || {}];
        setAllRowsItems(formatAllItems);
        setAllRowsData((prevAllRowsData: any) => [
            ...(prevAllRowsData || []),
            {
                ...(cloneData || {}),
                price: undefined,
                qty: undefined,
                // index: allRowsItems?.length || 1
            },
        ]);
    };

    const onChangeInputValueTable = (value: any, type?: string, index?: any, parent?: any) => {
        if ((type && index) || (index === 0 && type)) {
            const rowsDataFormat = (allRowsData && allRowsData?.length > 0) && allRowsData?.find((item: any, idx: number) => idx === index)
            const dataFormat = allRowsData && allRowsData?.length > 0
                ? allRowsData?.map((row: any, idx: number) => {
                    if ((idx === index)) {
                        return {
                            ...(row || {}),
                            [type]: type === 'price' ? /^\d*\.?\d*$/.test(value) ? value : rowsDataFormat?.[type] : value || undefined,
                        };
                    } else {
                        return row;
                    }
                })
                : [
                    {
                        [type]: type === 'price' ? /^\d*\.?\d*$/.test(value) ? value : rowsDataFormat?.[type] : value || undefined,
                    },
                ]
            setAllRowsData(dataFormat || []);
        }
    };

    const onChangeInput = (value: string | null | undefined, type: string) => {
        setFormFieldsData((prevFormFieldsData: any) => ({
            ...(prevFormFieldsData || {}),
            [type]: value,
        }));
    };

    const onChangeSelectedValue = (e: any, type?: string) => {
        if (type) {
            setFormFieldsData({
                ...(formFieldsData || {}),
                [type]: e ? e : undefined,
            });
        }
    };

    const onChangeDateInput = (e: any, type?: string) => {
        if (type) {
            setFormFieldsData({
                ...(formFieldsData || {}),
                [type]: e || undefined,
            });
        }
    };

    const onValidateItemsStep = () => {
        const rowErrors: any = [];
        if (allRowsData && allRowsData?.length > 0) {
            allRowsData?.map((item: any, idx: number) => {
                let itemErrors: any;
                if (!item.itemName) {
                    itemErrors = {
                        ...(itemErrors || {}),
                        itemName: 'Name is required',
                    };
                }
                if (!item.price) {
                    itemErrors = {
                        ...(itemErrors || {}),
                        price: 'Price is required',
                    };
                }
                if (!item.qty) {
                    itemErrors = {
                        ...(itemErrors || {}),
                        qty: 'Quantity is required',
                    };
                }
                if (itemErrors) {
                    rowErrors.push({ ...itemErrors, index: idx });
                }

                return item;
            });
        } else if (!allRowsData || allRowsItems?.length !== allRowsData?.length) {
            setItemsGeneralError('Please fill form to continue on the next step.');
            return false;
        }
        if (rowErrors && rowErrors?.length > 0) {
            setItemsErrors(rowErrors || undefined);
            setItemsGeneralError(undefined);
            return false;
        } else {
            setItemsErrors(undefined);
            setItemsGeneralError(undefined);
            return true;
        }
    };

    const onSubmitPurchaseForm = async () => {
        setLoadingSubmit(true)
        if (!validatePurchaseFields(formFieldsData, setFormFieldsErrors)) {
            setLoadingSubmit(false)
            return;
        }
        if (!onValidateItemsStep()) {
            setLoadingSubmit(false)
            return;
        }
        try {
            const formatItems = (allRowsData && allRowsData?.length > 0) && allRowsData?.map((item: any) => {
                delete item.index;
                return {
                    ...item,
                    price: parseFloat(item.price || 0),
                };
            });
            const purchaseDataForm = {
                supplierId: formFieldsData?.supplier?.value,
                shippingCosts: formFieldsData?.shippingCosts,
                expectedDeliveryDate: formFieldsData?.expectedDeliveryDate,
                currency: formFieldsData?.currency?.label,
                items: formatItems,
            };
            if (id) {
                const dataFormat = {
                    id,
                    data: purchaseDataForm,
                };
                await dispatch(tryToEditPurchase(dataFormat)).unwrap();
            } else {
                await dispatch(tryToCreatePurchase(purchaseDataForm)).unwrap();
            }
            document.body.style.overflow = 'auto';
            navigator('/purchase-orders');
            setShowConfirmModal(false)
        } catch (err: any) {
            setErrorStep(err?.response || err || 'Something went wrong!');
        }
        setLoadingSubmit(false)
    };

    const onClickSubmitConfirm = () => {
        if (!validatePurchaseFields(formFieldsData, setFormFieldsErrors)) {
            setLoadingSubmit(false)
            return;
        }
        if (!onValidateItemsStep()) {
            setLoadingSubmit(false)
            return;
        }
        if (showConfirmModal) {
            document.body.style.overflow = 'auto';
        } else {
            document.body.style.overflow = 'hidden';
        }
        setShowConfirmModal(!showConfirmModal)
    }

    // const totalPrice = allRowsData?.reduce((accumulator: number, item: any) => accumulator + (Number(item.price || 0) * Number(item?.qty || 0)), 0);
    // const totalQuantity = allRowsData?.reduce((accumulator: number, item: any) => accumulator + Number(item.qty || 0), 0);
    return (
        <div>
            <div className='mb-5'>
                <p className='page-title'> Purchases &gt; {id ? 'Edit' : 'Create'} Order</p>
            </div>
            {errorStep && (
                <div className='mt-4 px-4'>
                    <Error text={errorStep} />
                </div>
            )}
            <div className='p-4 pt-2 min-w-[100%]'>
                <FieldsStep
                    formData={formFieldsData}
                    formErrors={formFieldsErrors}
                    suppliers={suppliers}
                    currencies={currencies}
                    onChangeDateInput={onChangeDateInput}
                    onChangeInput={onChangeInput}
                    onChangeSelectedValue={onChangeSelectedValue}
                />
                <div className='bg-white mb-7'>
                    <ItemsStep
                        allRowsItems={allRowsItems}
                        itemsLoading={itemsLoading}
                        filterErrors={itemsErrors}
                        allRowsData={allRowsData}
                        itemsGeneralError={itemsGeneralError}
                        currency={formFieldsData?.currency?.label}
                        onCloneRow={onCloneRow}
                        onClickAddRow={onClickAddItems}
                        onDeleteItemRow={onDeleteItemRow}
                        onChangeInputValue={onChangeInputValueTable}
                    />
                </div>
                <div className='flex flex-row justify-center my-2'>
                    <Button
                        label='Submit purchase order'
                        className='btn-primary'
                        onClickButton={onClickSubmitConfirm}
                    />
                </div>
            </div>
            {showConfirmModal &&
                <ConfirmPurchaseCreate
                    openModal={showConfirmModal}
                    items={allRowsData}
                    edit={!!id || false}
                    detailsInfo={formFieldsData}
                    generalError={errorStep}
                    submitLoading={loadingSubmit}
                    currency={formFieldsData?.currency?.label}
                    handleCloseModal={onClickSubmitConfirm}
                    onApproveAction={onSubmitPurchaseForm}
                />
            }
        </div>
    );
};

export default PurchaseForm;